import { Link, useSearchParams } from 'react-router-dom';

import { MenuIcon } from '@/components/MenuIcon';
import { WidgetMasonry } from '@/components/widgets/WidgetMasonry';
import { isJudgeParty, useSession, type PartyType } from '@/lib/auth/session';
import { shortDateWithWeekday } from '@/lib/format';
import { useSiteGreeting } from '@/lib/queries';
import { useSelectedSite } from '@/lib/useSelectedSite';
import { useHomeLayout, type HomeLayout } from '@/pages/home/useHomeLayout';
import { useHomeWidgets } from '@/pages/home/useHomeWidgets';
import { WidgetManagerModal } from '@/pages/home/WidgetManagerModal';

/**
 * 홈 — 고른 현장의 대시보드. front 의 `/home` 과 같은 모양이다:
 * 인사말 한 줄 + 위젯 메이슨리 + 위젯 관리 모달.
 *
 * <p>여기에는 <b>위젯 이름이 한 줄도 없다.</b> 무엇을 어떤 순서로 그릴지는 등록부
 * (`pages/home/widgets/registry.ts`)와 사람이 고른 배치(`useHomeWidgets`)가 정한다.
 *
 * <p>현장을 고르는 자리는 <b>머리줄</b>이다({@link AppShell}) — 사이드바 메뉴가 전부 그 현장을
 * 가리키므로 앱 전체가 같은 하나를 봐야 한다. 여기서 또 고르게 두면 두 곳이 어긋난다.
 */
export function HomePage() {
  const { current, partyType, pending } = useSelectedSite();

  if (pending) {
    return <p className="py-6 text-center text-ui-sm" style={{ color: 'var(--text-muted)' }}>불러오는 중입니다…</p>;
  }

  if (current === undefined) {
    return (
      <p className="py-6 text-center text-ui-sm" style={{ color: 'var(--text-muted)' }}>
        배정된 현장이 없습니다.
      </p>
    );
  }

  return <SiteHome siteId={current.id} partyType={partyType} />;
}

/**
 * 현장이 정해진 뒤의 홈.
 *
 * <p>컴포넌트를 나눈 이유는 <b>훅 때문</b>이다. 위 화면은 현장이 없을 때 일찍 돌아가는데,
 * 그 뒤에 훅을 부르면 렌더마다 훅 개수가 달라져 React 가 상태를 잃는다. 현장이 확정된 자리에서
 * 훅을 부르도록 한 칸 내렸다.
 */
function SiteHome({ siteId, partyType }: { siteId: string; partyType: PartyType }) {
  const session = useSession();
  const widgets = useHomeWidgets(partyType);
  const { layout, setLayout } = useHomeLayout();
  // 판정하는 손 — 감리와 직원. 위젯이 「내가 답할 것」과 「내가 판정할 것」을 가르는 축이다(DOC-87 §3-3).
  const judge = isJudgeParty(partyType);

  /*
    위젯 관리를 여는 신호는 주소(`?widgets=1`)다. 사이드바 바닥의 「위젯 관리」가 다른 화면에서
    눌려도 홈으로 와서 열려야 하기 때문이다 — 바뀐 결과를 그 자리에서 눈으로 봐야 관리를 마칠 수 있다.
    홈 우상단의 「위젯」 버튼도 같은 신호를 준다. 닫을 때 주소에서 지운다 — 남겨 두면 새로고침이나
    뒤로가기마다 모달이 다시 열린다.
  */
  const [params, setParams] = useSearchParams();
  const managing = params.get('widgets') === '1';

  function openManager() {
    const next = new URLSearchParams(params);
    next.set('widgets', '1');
    setParams(next, { replace: true });
  }

  function closeManager() {
    const next = new URLSearchParams(params);
    next.delete('widgets');
    setParams(next, { replace: true });
  }

  const items = widgets.visible.map(({ definition, size }) => ({
    key: definition.widgetKey,
    title: definition.title,
    size,
    node: <definition.Component siteId={siteId} judge={judge} />,
  }));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {/*
          인사말 — front 의 `Greeting` 과 같은 자리. `lead`(이름) 뒤에 서버가 고른 「오늘의 한 마디」가
          이어 붙는다(`ExternalGreetingService` · DOC-84 §7 ⑥). 무엇을 말할지는 서버가 정하고 여기서는
          그리기만 한다. 못 받으면 이름과 날짜만 남는다 — 인사말은 재촉하는 장치이지 홈을 막을 이유가 아니다.
        */}
        <div className="min-w-0">
          <Greeting siteId={siteId} fallbackName={session.data?.name ?? ''} />
          <p className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>{shortDateWithWeekday(new Date())}</p>
        </div>

        {/* 인사말 줄의 오른쪽 끝 — 위젯을 어떻게 놓을지(격자형·자유형). 취향은 기기에 남는다(useHomeLayout). */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <LayoutToggle layout={layout} onChange={setLayout} />
          {/* 사이드바가 서랍으로 숨는 화면(lg 미만)에서는 여기가 위젯 관리로 가는 유일한 입구다. */}
          <button type="button" className="btn-secondary px-3 py-1.5 text-ui-xs lg:hidden" onClick={openManager}>
            <MenuIcon name="grid" size={13} />
            <span className="ml-1">위젯</span>
          </button>
        </div>
      </div>

      <WidgetMasonry items={items} layout={layout} />

      {managing && (
        <WidgetManagerModal
          slots={widgets.slots}
          customized={widgets.customized}
          onSave={widgets.save}
          onReset={widgets.reset}
          onClose={closeManager}
        />
      )}
    </div>
  );
}

/** 격자형 / 자유형 — 두 칸 토글. 출역 화면의 탭과 같은 옷이다. */
function LayoutToggle({ layout, onChange }: { layout: HomeLayout; onChange: (next: HomeLayout) => void }) {
  const options: Array<{ key: HomeLayout; label: string }> = [
    { key: 'grid', label: '격자형' },
    { key: 'masonry', label: '자유형' },
  ];
  return (
    <div className="flex gap-1 rounded-md p-1" style={{ background: 'var(--bg-hover)' }} role="tablist" aria-label="위젯 배치">
      {options.map((it) => {
        const active = it.key === layout;
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={active}
            className="rounded px-3 py-1 text-ui-xs"
            style={active
              ? { background: 'var(--bg-card)', color: 'var(--accent)', fontWeight: 600 }
              : { color: 'var(--text-secondary)' }}
            onClick={() => onChange(it.key)}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * 홈의 제목 줄 — front 의 `Greeting` 과 같은 모양.
 *
 * <p>`lead`(이름)는 <b>앵커</b>라 늘 앞에 있고, 좁은 화면에서 접히는 것은 뒤쪽 `body` 다.
 * `action` 이 있으면 `body` 가 그 화면으로 가는 링크가 된다 — 말을 걸고 끝나지 않는다.
 */
function Greeting({ siteId, fallbackName }: { siteId: string; fallbackName: string }) {
  const greeting = useSiteGreeting(siteId);
  const lead = greeting.data?.lead ?? `${fallbackName} 님`;
  const body = greeting.data?.body ?? null;
  const action = greeting.data?.action ?? null;

  return (
    <h1 className="text-ui-lg font-bold" style={{ wordBreak: 'keep-all' }}>
      {lead}
      {body !== null && (
        <>
          {/* 데스크톱은 한 줄, 좁은 화면은 두 줄. 문장이 길어져도 이름 줄은 깨지지 않는다. */}
          <span className="hidden md:inline">. </span>
          <span className="block md:inline" style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
            {action !== null ? (
              <Link to={action.href} className="inline-flex items-center gap-0.5 hover:underline" title={action.label}>
                {body}
                <MenuIcon name="chevron-right" size={13} />
              </Link>
            ) : (
              body
            )}
          </span>
        </>
      )}
    </h1>
  );
}
